/**
 * Custom Error type used when a pool is not found.
 */
class PoolNotFoundError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PoolNotFoundError';
  }
} 

/**
 * The base class for a card pool source identifier.
 */
class SourceID {
  /**
   * @param {*} toSourceIdFn a function that wraps an identifier in an appropriate SourceID type
   * @returns a function that accepts JSON and converts a `sourceId` attribute to the JSON 
   * reprseentation of the appropriate SourceID type
   */
  static wrapJsonSourceId(toSourceIdFn) {
    return (json) => {
      if (json.sourceId) {
        // convert json.sourceId to the correct SourceID type before converting 
        // `toJSON` for the appropriate format from which to restore a SourceID
        const sourceId = toSourceIdFn(json.sourceId);
        return { ...json, sourceId: sourceId.toJSON() }
      }
      return json;
    }
  }
    /**
   * Deserialize the appropriate SourceID from the provided source ID JSON data.
   * 
   * @param {Object} sourceJson  
   * @returns the deserialized SourceID
   */
  static fromJSON(jsonSourceId) {
    if (jsonSourceId.startsWith(RemotePoolStateID.PREFIX)) {
      return new RemotePoolStateID(jsonSourceId.slice(RemotePoolStateID.PREFIX.length))
    }
    else if (jsonSourceId.startsWith(RemoteTierListID.PREFIX)) {
      return new RemoteTierListID(jsonSourceId.slice(RemoteTierListID.PREFIX.length))
    }
    // check SeventeenLands TierList first because the prefix is more specific
    else if (jsonSourceId.startsWith(SeventeenLandsTierListID.PREFIX)) {
      return new SeventeenLandsTierListID(jsonSourceId.slice(SeventeenLandsTierListID.PREFIX.length))
    }
    else if (jsonSourceId.startsWith(SeventeenLandsPoolID.PREFIX)) {
      return new SeventeenLandsPoolID(jsonSourceId.slice(SeventeenLandsPoolID.PREFIX.length))
    }
  }

  prefix() {
    throw new Error(`${this.constructor.name}.prefix() is not implemented`)
  }

  /**
   * If an object being stringified (by `JSON.stringify`) has a property named `toJSON` whose 
   * value is a function, then the toJSON() method customizes JSON stringification behavior: 
   * instead of the object being serialized, the value returned by the toJSON() method when 
   * called will be serialized.
   */
  toJSON() {
    return `${this.prefix()}${this.id}`;
  }

  /**
   * @returns a JSON object representation of this SourceID
   */
  toJsonObject() {
    throw new Error(`${this.constructor.name}.toJsonObject() is not implemented`)
  }
}

/**
 * Source identifier for a card pool state in remote storage
 */
class RemotePoolStateID extends SourceID {
  static PREFIX = 'pool:';

  constructor(id) {
    super();
    this.id = id;
  }

  prefix() {
    return RemotePoolStateID.PREFIX;
  }

  /**
   * @returns a JSON object representation of this SourceID
   */
  toJsonObject() {
    return { 'source_poolstate_id': this.id };
  }
}

/**
 * Source identifier for a tier list in remote storage
 */
class RemoteTierListID extends SourceID {
  static PREFIX = 'tierlist:';

  constructor(id) {
    super();
    this.id = id;
  }

  prefix() {
    return RemoteTierListID.PREFIX;
  }

  /**
   * @returns a JSON object representation of this SourceID
   */
  toJsonObject() {
    return { 'source_tierlist_id': this.id };
  }
}

/**
 * Source identifier for a 17Lands card pool or deck
 */
class SeventeenLandsPoolID extends SourceID {
  static PREFIX = '17lands-';

  constructor(poolId, deckId) {
    super();
    this.id = deckId ? `${poolId}-${deckId}` : poolId;
  }

  prefix() {
    return SeventeenLandsPoolID.PREFIX;
  }

  /**
   * @returns a JSON object representation of this SourceID
   */
  toJsonObject() {
    return { 'source_external': `${this.prefix()}${this.id}` };
  }
}

/**
 * Source identifier for a 17Lands tier list
 */
class SeventeenLandsTierListID extends SourceID {
  static PREFIX = '17lands-tierlist-';

  static checkType(sourceId) {
    return sourceId && sourceId.prefix() === SeventeenLandsTierListID.PREFIX;
  }

  constructor(tierlistId) {
    super();
    this.id = tierlistId;
  }

  prefix() {
    return SeventeenLandsTierListID.PREFIX;
  }

  /**
   * @returns a JSON object representation of this SourceID
   */
  toJsonObject() {
    return { 'source_external': `${this.prefix()}${this.id}` };
  }  
}

/* Explicit CommonJS module exports */
module.exports = {
  PoolNotFoundError,
  RemotePoolStateID,
  RemoteTierListID,
  SeventeenLandsPoolID,
  SeventeenLandsTierListID,
  SourceID
};